import { isEmpty } from "es-toolkit/compat";
import { atom } from "jotai";

import { DocumentMetadataSchema } from "@moment/api-collab/api-types";
import { Logger } from "@moment/logging";

import { $accessTokenFunc } from "../authenication";
import {
	$addDraftDocument,
	$documentsList,
	type DocsListItem,
	docsListItemRemoteEntry,
	isViewerPermission,
} from "../document-list";
import { $purgeDocument } from "../document-loader";
import { $registerDocument } from "../document-loader/registry.atom";
import { $setCollabTrackingBranch, $switchToCollabBranch } from "../document/sync/mutations.atom";
import { env } from "../env";
import { fs } from "../filesystem";
import { $git, $gitStatus } from "../git";
import { getDocumentDirPath, paths } from "../paths";
import { $getWorkspace } from "../workspaces";
import { $isCloning } from "./clone-state.atom";

const logger = new Logger("clone.workflow.atom");

export class CloneGitCloneError extends Error {
	public readonly tag = "clone-git-clone-error" as const;
	constructor(remoteUrl: string, error: unknown) {
		const msg =
			error instanceof Error
				? error.message
				: typeof error === "string"
					? error
					: "unknown reason";
		super(`Git clone failed for remote URL ${remoteUrl}: ${msg}`);
		this.name = "CloneGitCloneError";
	}
}

export class CloneWorkspaceNotFoundError extends Error {
	public readonly tag = "clone-workspace-not-found-error" as const;
	constructor(workspaceName: string) {
		super(`Workspace not found: ${workspaceName}`);
		this.name = "CloneWorkspaceNotFoundError";
	}
}

export class CloneMetadataError extends Error {
	public readonly tag = "clone-metadata-error" as const;
	constructor(documentId: string, cause: unknown) {
		super(`Failed to read metadata for cloned document ${documentId}`);
		this.name = "CloneMetadataError";
		this.cause = cause;
	}
}

export class CloneViewerPermissionError extends Error {
	public readonly tag = "clone-viewer-permission-error" as const;
	constructor(documentId: string) {
		super(`Cannot clone document ${documentId}: viewer permission does not allow cloning`);
		this.name = "CloneViewerPermissionError";
	}
}

export class CloneDocumentNotReadyError extends Error {
	public readonly tag = "clone-document-not-ready-error" as const;
	constructor(documentId: string) {
		super(`Cannot clone document ${documentId}: document permissions are still loading`);
		this.name = "CloneDocumentNotReadyError";
	}
}

export const $cloneWorkflow = atom(
	null,
	async (get, set, input: { workspaceName: string; documentId: string }) => {
		const { workspaceName, documentId } = input;

		set($isCloning(documentId), true);
		logger.debug("Starting clone workflow", { input });

		try {
			const documentsList = get($documentsList);
			const existingDocument = documentsList.documents[documentId];

			// Fail closed until the docs list has hydrated. Viewer permissions arrive with the
			// docs list payload, so proceeding before that state exists can incorrectly allow clone.
			if (documentsList.status === "initializing" || !existingDocument) {
				throw new CloneDocumentNotReadyError(documentId);
			}

			if (isViewerPermission(existingDocument)) {
				throw new CloneViewerPermissionError(documentId);
			}

			const git = get($git);
			const pathsApi = paths();
			const fsApi = fs();

			const documentPath = await getDocumentDirPath(documentId);
			logger.debug("cloning to path", { path: documentPath });

			const remoteUrl = `${env.docsApiUrl}/v2/git/${workspaceName}/${documentId}.git`;
			const token = await get($accessTokenFunc)?.getAccessTokenSilently?.();
			const workspace = await get($getWorkspace(workspaceName));
			logger.debug("Found workspace", { workspaceName, workspace });

			if (!workspace) {
				throw new CloneWorkspaceNotFoundError(workspaceName);
			}

			logger.debug("Cloning document", {
				documentId,
				workspaceName,
				remoteUrl,
				path: documentPath,
			});
			try {
				const result = await git.clone({ path: documentPath, remoteUrl, token });
				logger.debug("Git clone result", { result });
			} catch (error) {
				throw new CloneGitCloneError(remoteUrl, error);
			}

			// Read metadata from the cloned document
			const metadataFilePath = await get(pathsApi.getDocumentMetadataFilePath(documentId));
			let metadata;
			try {
				const parsed = await get(fsApi.readYamlFile({ filePath: metadataFilePath }));
				const result = DocumentMetadataSchema.safeParse(parsed);
				if (result.error) {
					throw result.error;
				}
				metadata = result.data;
			} catch (error) {
				throw new CloneMetadataError(documentId, error);
			}

			// Ensure dates are valid
			const createdAt =
				metadata.created && !isNaN(metadata.created.getTime())
					? metadata.created
					: new Date();
			const updatedAt =
				metadata.updated && !isNaN(metadata.updated.getTime())
					? metadata.updated
					: createdAt;

			// Preserve remote/collab info from the existing document (if any)
			const existingRemote = existingDocument?.remote ?? {};

			// Build DocsListItem with local entry and preserved remote info
			const docsListItem: DocsListItem = {
				id: documentId,
				local: {
					[documentPath]: {
						path: documentPath,
						title: metadata.title,
						createdAt,
						updatedAt,
						type: metadata.type,
					},
				},
				remote: existingRemote,
			};

			// Add to draft documents so it appears immediately in the documents list
			set($addDraftDocument, docsListItem);
			logger.debug("Added cloned document to draft documents", { documentId, documentPath });

			// If the document has active collab sessions, switch to collab/main
			const remoteEntry = docsListItemRemoteEntry(docsListItem);
			const hasCollab = remoteEntry?.collab && !isEmpty(remoteEntry.collab);
			if (hasCollab) {
				logger.debug("Document has active collab, switching to collab/main", {
					documentId,
				});
				await set($switchToCollabBranch, documentId);
				await set($setCollabTrackingBranch, documentId);
			}

			// Purge any previously loaded state so the loader re-evaluates from scratch.
			// This clears loadedKeys and unloads metadata/editor/request states.
			set($purgeDocument, documentId);

			// Ensure the document is registered so the loader effect tracks it
			// (it may already be tracked via the URL, but this guarantees it).
			set($registerDocument, documentId);

			// Refresh git status and await the result so the document loader sees
			// the correct branch/commit when it next runs. Without this await, the
			// loader may see a stale or loading status and skip the document.
			set($gitStatus(documentId));
			await get($gitStatus(documentId));

			logger.info("Clone workflow completed", { documentId, workspaceName });
		} catch (error) {
			logger.error("Clone workflow failed", { error });
			throw error;
		} finally {
			set($isCloning(documentId), false);
		}
	}
);
